// EventsCreateEvent – create a new event (name, start/end).

import React, { JSX, useEffect, useState } from 'react';
import { formatTs } from '../../../helpers/helpers';
import { dispatchToast, dispatchToastError } from '../utils';
import InlineSpinner from '../../shared/InlineSpinner';

type EventRow = {
    eventId: string;
    name?: string;
    status?: string;
    createdAt?: number;
    startAt?: number;
    endAt?: number;
    [key: string]: unknown;
};

type CreateEventFormState = {
    name: string;
    start: string; // ISO string for datetime-local input
    end: string;   // ISO string for datetime-local input
};

// ---- Helpers ----

function toUnixSeconds(value: string): number | undefined {
    if (!value) return undefined;
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts) || ts <= 0) return undefined;
    return Math.floor(ts / 1000);
}

// ---- Component ----

export default function EventCreateSection(): JSX.Element {
    const [events, setEvents] = useState<EventRow[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);

    const [form, setForm] = useState<CreateEventFormState>({
        name: '',
        start: '',
        end: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const [eventsApiMissing] = useState(() => !window.eventsAPI);

    useEffect(() => {
        if (!window.eventsAPI) return;
        void loadEvents();
    }, []);

    // Fetch events list for the table and any error toasts.
    async function loadEvents(): Promise<void> {
        if (!window.eventsAPI) {
            const msg = 'Events API is not available';
            dispatchToast('toast-error', msg);
            return;
        }

        setEventsLoading(true);

        try {
            const res = await window.eventsAPI.listEvents({ limit: 200 });
            if (!res.ok) {
                const msg = `Failed to load events (${res.status ?? 'error'})`;
                dispatchToast('toast-error', msg);
                return;
            }

            const data = res.data as any;
            const rows: EventRow[] = Array.isArray(data?.events) ? data.events : [];
            setEvents(rows);
        } catch (err: any) {
            dispatchToastError(err, 'Failed to load events');
        } finally {
            setEventsLoading(false);
        }
    }

    function handleChange(
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ): void {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    async function handleSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();

        if (!window.eventsAPI) {
            dispatchToast('toast-error', 'Events API is not available');
            return;
        }

        if (!form.name.trim()) {
            dispatchToast('toast-error', 'Event name is required');
            return;
        }

        const startAt = toUnixSeconds(form.start);
        const endAt = toUnixSeconds(form.end);

        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                startAt,
                endAt,
            };

            const res = await window.eventsAPI.createEvent(payload);

            if (!res.ok) {
                dispatchToast(
                    'toast-error',
                    `Failed to create event (${res.status ?? 'error'})`,
                );
                return;
            }

            dispatchToast('toast-info', 'Event created successfully');

            setForm({
                name: '',
                start: '',
                end: '',
            });

            await loadEvents();
        } catch (err: any) {
            dispatchToastError(err, 'Failed to create event');
        } finally {
            setSubmitting(false);
        }
    }

    if (eventsApiMissing) {
        return (
            <div className="px-4 py-3 text-xs text-red-700" role="alert">
                Events API is not available
            </div>
        );
    }

    return (
        <div className="px-4 py-3 space-y-4 text-xs">
            {/* Events list (viewer only) */}
            <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                        Events
                    </h3>
                    {eventsLoading && (
                        <span className="text-[11px] text-gray-500 dark:text-slate-400">
                            Loading events…
                        </span>
                    )}
                </div>

                {events.length === 0 && !eventsLoading ? (
                    <div className="text-[11px] text-gray-500 dark:text-slate-400">
                        No events found.
                    </div>
                ) : (
                    <div className="max-h-40 overflow-auto border border-gray-100 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800">
                        <table className="min-w-full text-[11px]">
                            <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Name
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Event ID
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-slate-300">
                                        Start
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                                {events.map((ev) => (
                                    <tr key={ev.eventId}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-gray-900 dark:text-slate-100">
                                                {ev.name || '(untitled)'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="font-mono text-[11px] break-all text-gray-700 dark:text-slate-300">
                                                {ev.eventId}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-slate-300">
                                            {formatTs(ev.startAt as number | undefined)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
                    Existing events are listed above for reference.
                </p>
            </div>

            {/* Create event form */}
            <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                        Create new event
                    </h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                            Event name
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Ironman Taupō 2026"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                                Start time (optional)
                            </label>
                            <input
                                type="datetime-local"
                                name="start"
                                value={form.start}
                                onChange={handleChange}
                                className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                                End time (optional)
                            </label>
                            <input
                                type="datetime-local"
                                name="end"
                                value={form.end}
                                onChange={handleChange}
                                className="mt-1 w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                            {submitting ? (
                                <>
                                    <InlineSpinner size="xs" variant="current" />
                                    Creating…
                                </>
                            ) : (
                                'Create event'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
